/**
 * Page d'accueil – portage de design/components/bundle.js (HomeGrid, Widget, QuickLinks, RecentModules, RecentStack,
 * WeatherIcon, WeatherWidget, WorldClocks, NewsList, SocialFeed).
 * Éléments interactifs sur Radix : catalogue « Ajouter une carte » = menu déroulant ; réglages d'une carte = fenêtre non
 * modale Radix Dialog affichée à sa place (Échap, clic à côté, focus rendu au bouton).
 */
import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cx, h, Icon, ModuleTag, StatusBadge, type IconName, type Metier, type Status } from './base';
import { NewsVisual, type Scene } from './actualite';
import { ShapeFlag } from './filiale';
import { Menu, type MenuProps } from './navigation';

/* ---------- HomeGrid ---------- */
export interface HomeGridProps {
  editing?: boolean; addLabel?: string; children?: React.ReactNode;
  /**
   * Ajouts AGORA : tuile « + Ajouter une carte ». `addMenu` = catalogue des cartes en menu déroulant Radix ouvert par
   * la tuile (clavier, Échap, clic à côté) ; `onAdd` + `addPopover` (contenu ancré géré par l'appelant) restent admis.
   */
  onAdd?: () => void; addPopover?: React.ReactNode; addMenu?: Omit<MenuProps, 'trigger'>;
  /** Ajouts AGORA : grille de cases régulières (enfants = cases `agx-case`), référence de la grille (déplacement des cartes). */
  cells?: boolean; gridRef?: React.Ref<HTMLDivElement>;
}

export function HomeGrid(p: HomeGridProps) {
  const tuile = h('button', { type: 'button', className: 'ag-widget-add', onClick: p.onAdd, 'aria-haspopup': 'menu' }, h(Icon, { name: 'plus', size: 22 }), h('span', null, p.addLabel));
  return h('div', { ref: p.gridRef, className: cx('ag-home-grid', p.editing && 'ag-home-editing', p.cells && 'agx-grille-cases') }, p.children,
    p.editing ? h('div', { className: 'agx-widget-add-host' },
      p.addMenu ? h(Menu, { side: 'bottom', align: 'start', ...p.addMenu, trigger: tuile }) : tuile,
      p.addPopover || null) : null);
}

/* ---------- Widget ---------- */
export interface WidgetProps {
  title?: string; link?: string; span?: 1 | 2 | 3 | 4; rows?: number; editing?: boolean; removeLabel?: string; bare?: boolean; children?: React.ReactNode;
  /** Ajouts AGORA : lien, retrait, mention complémentaire (ex. « Données d'exemple »). */
  href?: string; onRemove?: () => void; extra?: React.ReactNode;
  /**
   * Ajouts AGORA – mode personnalisation : poignée focalisable libellée `moveLabel` (le déplacement, clavier compris,
   * est géré par la grille parente) ; réglages de la carte (ex. mode d'affichage) dans une fenêtre ouverte par le
   * bouton `toolsLabel` de l'en-tête.
   */
  moveLabel?: string; tools?: React.ReactNode; toolsLabel?: string;
}

export function Widget(p: WidgetProps) {
  const span = p.span || 1, rows = p.rows || 1;
  const [outils, setOutils] = React.useState(false);
  const ouvert = outils && !!p.editing && !!p.tools;
  return h('section', {
    className: cx('ag-widget', !p.bare && 'ag-glass-card', p.editing && 'ag-widget-editing', ouvert && 'agx-widget-outils-ouverts'),
    style: { gridColumn: 'span ' + span, gridRow: 'span ' + rows }, 'aria-label': p.title,
  },
  p.title || p.editing ? h('header', { className: 'ag-widget-head' },
    p.editing ? (p.moveLabel
      ? h('button', { type: 'button', className: 'ag-ts-grip', 'aria-label': p.moveLabel }, h(Icon, { name: 'grip', size: 16 }))
      : h('span', { className: 'ag-ts-grip', 'aria-hidden': true }, h(Icon, { name: 'grip', size: 16 }))) : null,
    p.title ? h('b', null, p.title) : null,
    p.extra && !p.editing ? p.extra : null,
    p.link && !p.editing ? h('a', { href: p.href || '#', className: 'ag-login-link' }, p.link) : null,
    p.editing && p.tools ? h(Dialog.Root, { open: ouvert, onOpenChange: setOutils, modal: false },
      h('div', { className: 'agx-widget-outil-hote' },
        h(Dialog.Trigger, { asChild: true },
          h('button', { type: 'button', className: 'agx-widget-outil', 'aria-label': p.toolsLabel, title: p.toolsLabel }, h(Icon, { name: 'settings', size: 14 }))),
        h(Dialog.Content, { className: cx('agx-widget-pop', 'ag-glass', 'ag-glass-strong'), 'aria-describedby': undefined },
          h(Dialog.Title, { className: 'agx-annonce' }, p.toolsLabel), p.tools))) : null,
    p.editing ? h('button', { type: 'button', className: 'ag-widget-x', 'aria-label': `${p.removeLabel || ''} ${p.title || ''}`.trim(), onClick: p.onRemove }, h(Icon, { name: 'minus', size: 14 })) : null) : null,
  h('div', { className: 'ag-widget-body' }, p.children));
}

/* ---------- QuickLinks ---------- */
export interface QuickLinksProps { compact?: boolean; items: { id: string; label: string; path?: string; icon: IconName; metier: Metier; href?: string }[] }

export function QuickLinks(p: QuickLinksProps) {
  return h('div', { className: cx('ag-quick', p.compact && 'ag-quick-compact') }, (p.items || []).map((it) =>
    h('a', { key: it.id, href: it.href || '#', className: 'ag-quick-tile' },
      h('span', { className: cx('ag-quick-ico', 'ag-mod-' + it.metier) }, h(Icon, { name: it.icon, size: 20 })),
      h('span', { className: 'ag-quick-label' }, it.label), it.path ? h('small', null, it.path) : null)));
}

/* ---------- RecentModules ---------- */
/** `time` facultatif (extension AGORA) : sans valeur, la colonne de l'heure n'est pas rendue. */
export interface RecentModulesProps { items: { id: string; label: string; path: string; metier: Metier; time?: string; href?: string }[] }

export function RecentModules(p: RecentModulesProps) {
  return h('ol', { className: 'ag-recent-mods' }, (p.items || []).slice(0, 5).map((it) =>
    h('li', { key: it.id }, h('a', { href: it.href || '#' },
      h('span', { className: cx('ag-side-mod', 'ag-mod-' + it.metier) }),
      h('span', { className: 'ag-recent-txt' }, h('small', null, it.path), h('b', null, it.label)),
      it.time ? h('span', { className: 'ag-recent-time' }, it.time) : null))));
}

/* ---------- RecentStack (derniers objets consultés) ---------- */
export interface RecentStackProps {
  step?: number;
  items: { id: string; reference: string; title: string; metier: Metier; moduleLabel: string; status?: Status; statusLabel?: string; detail?: string; time?: string; href?: string }[];
}

export function RecentStack(p: RecentStackProps) {
  const items = p.items || [], n = items.length, step = p.step || 42;
  return h('div', { className: 'ag-stack', style: { height: (n - 1) * step + 156 } }, items.map((it, i) => {
    const devant = i === n - 1;
    return h('a', { key: it.id, href: it.href || '#', className: cx('ag-stack-card', devant && 'ag-stack-front', 'ag-mod-' + it.metier), style: { top: i * step, zIndex: i + 1 } },
      h('div', { className: 'ag-stack-top' }, h('i', { className: 'ag-stack-dot' }), h('b', null, it.title), h('span', { className: 'ag-stack-ref' }, it.reference)),
      devant ? h('div', { className: 'ag-stack-body' },
        h('div', { className: 'ag-stack-meta' }, h(ModuleTag, { metier: it.metier, label: it.moduleLabel }), it.status ? h(StatusBadge, { status: it.status, label: it.statusLabel || '' }) : null),
        it.detail ? h('div', { className: 'ag-stack-detail' }, it.detail) : null,
        h('div', { className: 'ag-stack-time' }, it.time)) : null);
  }));
}

/* ---------- Météo ---------- */
export type WeatherKind = 'sun' | 'partly' | 'cloud' | 'rain';

export function WeatherIcon(p: { kind?: WeatherKind; size?: number }) {
  const k = p.kind || 'sun', sz = p.size || 40;
  const soleil = h('g', { key: 's' }, h('circle', { cx: 15, cy: 15, r: 7, fill: '#f6b93b' }), [0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
    const r = (a * Math.PI) / 180;
    return h('line', { key: a, x1: 15 + Math.cos(r) * 10, y1: 15 + Math.sin(r) * 10, x2: 15 + Math.cos(r) * 13, y2: 15 + Math.sin(r) * 13, stroke: '#f6b93b', strokeWidth: 2, strokeLinecap: 'round' });
  }));
  const nuage = (x: number, y: number, c?: string) => h('path', { key: 'c' + x, d: `M${x + 8} ${y + 18}h18a7 7 0 0 0 0-14 9 9 0 0 0-17-2 6 6 0 0 0-1 16z`, fill: c || '#dfe7ea', stroke: 'rgba(0,0,0,0.12)', strokeWidth: 0.8 });
  const parties = k === 'sun' ? [soleil] : k === 'partly' ? [soleil, nuage(10, 14)]
    : k === 'rain' ? [nuage(4, 6, '#b9c6cc'), [0, 1, 2].map((i) => h('line', { key: 'r' + i, x1: 14 + i * 7, y1: 28, x2: 11 + i * 7, y2: 35, stroke: '#3b8fd4', strokeWidth: 2, strokeLinecap: 'round' }))]
      : [nuage(4, 8, '#c9d4d8')];
  return h('svg', { width: sz, height: sz, viewBox: '0 0 40 40', 'aria-hidden': true, className: 'ag-wx-ico' }, parties);
}

export interface WeatherWidgetProps {
  place: string; siteLabel?: string; temp: number | string; unit?: string; condition: string; kind: WeatherKind;
  details?: { label: string; value: string }[]; forecast?: { day: string; kind: WeatherKind; high: number; low: number }[]; alert?: string;
  /**
   * Ajout AGORA – mode d'affichage : « compact » (température et localité sur une ligne, condition lue par les
   * lecteurs d'écran), « moyen » (sans détails, prévisions resserrées), « complet » (défaut, vue du design).
   */
  mode?: 'compact' | 'moyen' | 'complet';
}

export function WeatherWidget(p: WeatherWidgetProps) {
  if (p.mode === 'compact') {
    return h('div', { className: 'ag-wx agx-wx-compact', title: p.condition },
      h('div', { className: 'ag-wx-now' }, h(WeatherIcon, { kind: p.kind, size: 32 }),
        h('div', { className: 'ag-wx-temp' }, p.temp, h('small', null, p.unit || '°C')),
        h('div', { className: 'ag-wx-place' }, h(Icon, { name: 'pin', size: 14 }), h('span', null, p.place)),
        h('span', { className: 'agx-annonce' }, p.condition)));
  }
  if (p.mode === 'moyen') {
    return h('div', { className: 'ag-wx agx-wx-moyen' },
      h('div', { className: 'ag-wx-now' }, h(WeatherIcon, { kind: p.kind, size: 40 }),
        h('div', null, h('div', { className: 'ag-wx-temp' }, p.temp, h('small', null, p.unit || '°C')), h('div', { className: 'ag-wx-cond' }, p.condition))),
      h('div', { className: 'ag-wx-place' }, h(Icon, { name: 'pin', size: 14 }), h('span', null, p.place), p.siteLabel ? h('small', null, ' · ' + p.siteLabel) : null),
      p.forecast ? h('div', { className: 'ag-wx-fc' }, p.forecast.map((f, i) =>
        h('div', { key: i, className: 'ag-wx-day' }, h('small', null, f.day), h(WeatherIcon, { kind: f.kind, size: 20 }),
          h('span', null, h('b', null, f.high + '°'), ' ' + f.low + '°')))) : null,
      p.alert ? h('div', { className: 'ag-wx-alert' }, h(Icon, { name: 'bell', size: 14 }), p.alert) : null);
  }
  return h('div', { className: 'ag-wx' },
    h('div', { className: 'ag-wx-now' }, h(WeatherIcon, { kind: p.kind, size: 52 }),
      h('div', null, h('div', { className: 'ag-wx-temp' }, p.temp, h('small', null, p.unit || '°C')), h('div', { className: 'ag-wx-cond' }, p.condition))),
    h('div', { className: 'ag-wx-place' }, h(Icon, { name: 'pin', size: 14 }), h('span', null, p.place), p.siteLabel ? h('small', null, ' · ' + p.siteLabel) : null),
    p.details ? h('div', { className: 'ag-wx-details' }, p.details.map((d, i) => h('span', { key: i }, h('small', null, d.label), d.value))) : null,
    p.forecast ? h('div', { className: 'ag-wx-fc' }, p.forecast.map((f, i) =>
      h('div', { key: i, className: 'ag-wx-day' }, h('small', null, f.day), h(WeatherIcon, { kind: f.kind, size: 26 }), h('b', null, f.high + '°'), h('span', null, f.low + '°')))) : null,
    p.alert ? h('div', { className: 'ag-wx-alert' }, h(Icon, { name: 'bell', size: 14 }), p.alert) : null);
}

/* ---------- WorldClocks (heure des filiales) ---------- */
export interface WorldClocksProps {
  clocks: { label: string; timeZone: string; country?: string }[]; homeZone?: string; locale?: string; live?: boolean; at?: string; sameLabel?: string;
  /** Ajout AGORA : lignes resserrées (mode « moyen » de la carte). */
  dense?: boolean;
}

export function WorldClocks(p: WorldClocksProps) {
  const [maintenant, setMaintenant] = React.useState(Date.now());
  React.useEffect(() => {
    if (!p.live) return;
    const t = window.setInterval(() => setMaintenant(Date.now()), 30000);
    return () => window.clearInterval(t);
  }, [p.live]);
  const base = p.at ? new Date(p.at) : new Date(maintenant);
  const parties = (tz: string) => {
    const f = new Intl.DateTimeFormat(p.locale || 'fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'short' }).formatToParts(base);
    const o: Record<string, string> = {};
    f.forEach((x) => { o[x.type] = x.value; });
    return o;
  };
  const ici = parties(p.homeZone || 'Europe/Paris');
  return h('ul', { className: cx('ag-clocks', p.dense && 'agx-clocks-dense') }, (p.clocks || []).map((c) => {
    const o = parties(c.timeZone), hh = +o.hour % 24, mm = +o.minute, jour = hh >= 7 && hh < 19;
    const diff = ((hh - (+ici.hour % 24) + 24 + 12) % 24) - 12, memeJour = o.weekday === ici.weekday;
    const ha = ((hh % 12) + mm / 60) * 30, ma = mm * 6;
    return h('li', { key: c.timeZone + c.label, className: cx('ag-clock', jour ? 'ag-clock-day' : 'ag-clock-night') },
      h('svg', { className: 'ag-clock-face', viewBox: '0 0 40 40', width: 40, height: 40, 'aria-hidden': true },
        h('circle', { cx: 20, cy: 20, r: 18.5 }),
        [0, 90, 180, 270].map((a) => h('line', { key: a, x1: 20, y1: 4, x2: 20, y2: 7, transform: `rotate(${a} 20 20)`, className: 'ag-clock-tick' })),
        h('line', { x1: 20, y1: 20, x2: 20, y2: 11, transform: `rotate(${ha} 20 20)`, className: 'ag-clock-h' }),
        h('line', { x1: 20, y1: 21, x2: 20, y2: 7, transform: `rotate(${ma} 20 20)`, className: 'ag-clock-m' }),
        h('circle', { cx: 20, cy: 20, r: 1.6, className: 'ag-clock-c' })),
      h('span', { className: 'ag-clock-txt' }, h('b', null, c.label),
        h('small', null, (diff === 0 ? p.sameLabel || '' : (diff > 0 ? '+' : '') + diff + ' h') + (memeJour ? '' : ' · ' + o.weekday))),
      c.country ? h(ShapeFlag, { country: c.country, size: 18 }) : null,
      h('span', { className: 'ag-clock-time' }, o.hour + ':' + o.minute));
  }));
}

/* ---------- NewsList (widget actualité) ---------- */
export interface NewsListProps { featured?: boolean; items: { title: string; date?: string; tag?: string; metier?: Metier; excerpt?: string; image?: string; scene?: Scene; href?: string }[] }

export function NewsList(p: NewsListProps) {
  return h('div', { className: 'ag-newslist' }, (p.items || []).map((it, i) =>
    h('a', { key: i, href: it.href || '#', className: cx('ag-newsitem', i === 0 && p.featured && 'ag-newsitem-feat') },
      h('span', { className: 'ag-newsitem-img' }, h(NewsVisual, it)),
      h('span', { className: 'ag-newsitem-txt' },
        h('small', null, it.date, it.tag ? h('i', { className: cx('ag-crumb-dot', 'ag-mod-' + (it.metier || 'service')) }) : null, it.tag ? it.tag : null),
        h('b', null, it.title),
        it.excerpt && i === 0 && p.featured ? h('span', { className: 'ag-newsitem-ex' }, it.excerpt) : null))));
}

/* ---------- SocialFeed (page LinkedIn de l'entreprise) ---------- */
export interface SocialFeedProps {
  author: string; avatar?: React.ReactNode; sourceLabel: string; openLabel: string;
  posts: { time: string; text: string; image?: string; scene?: Scene; likes: number | string; comments: number | string; shares: number | string; href?: string }[];
}

export function SocialFeed(p: SocialFeedProps) {
  return h('div', { className: 'ag-feed' }, (p.posts || []).map((po, i) =>
    h('article', { key: i, className: 'ag-post' },
      h('header', { className: 'ag-post-head' }, h('span', { className: 'ag-post-av' }, p.avatar || (p.author || '?').slice(0, 1)),
        h('span', { className: 'ag-post-who' }, h('b', null, p.author), h('small', null, po.time)), h('span', { className: 'ag-post-src' }, p.sourceLabel)),
      h('p', { className: 'ag-post-txt' }, po.text),
      po.scene || po.image ? h('span', { className: 'ag-post-img' }, h(NewsVisual, po)) : null,
      h('footer', { className: 'ag-post-foot' },
        h('span', null, h(Icon, { name: 'thumb', size: 14 }), po.likes), h('span', null, h(Icon, { name: 'chat', size: 14 }), po.comments),
        h('span', null, h(Icon, { name: 'share', size: 14 }), po.shares),
        h('a', { href: po.href || '#', className: 'ag-login-link' }, p.openLabel, ' ', h(Icon, { name: 'arrow', size: 12 }))))));
}
